import { types, getParent, flow } from 'mobx-state-tree';
import PricingStrategy from '../../models/SettingsStore/PricingStrategy';
import PricingRange from '../../models/SettingsStore/PricingRange';

const Pricing = types
  .model('pricing', {
    pricingList: types.optional(PricingStrategy, {}),
    rangedPricingList: types.optional(types.array(PricingRange), []),
    isUpdatingAllStore: false
  })
  .views(self => ({
    get transport5007() {
      return getParent(self).transport5007;
    },
    get getPricingList() {
      return self.pricingList;
    },
    get currencyId() {
      return getParent(self).userProfile.defaultCurrencyId;
    },
    get changedRangedPricingList() {
      return self.rangedPricingList
        .filter(({ isChanged }) => isChanged)
        .map(({ breakEven, inPercents, maxValue, minValue, profit }) => ({
          breakEven,
          inPercents,
          maxValue,
          minValue,
          profit,
          currency: self.currencyId,
        }));
    },
  }))
  .actions(self => {
    const setIsUpdatingAllStore = (val)=>{
      self.isUpdatingAllStore=val
    };
    const fetchPricingList = flow(function* fetchPricingList(
      srcId = getParent(self).markets.defaultSource?.id ?? '',
      destId = getParent(self).markets.defaultDestination?.id ?? '',
      sellersCode = getParent(self).markets.defaultSellersAccount?.id ?? '',
    ) {
      const { accountCode } = getParent(self);
      try {
        const { data } =
          srcId && sellersCode
            ? yield self.transport5007.fetch(
                `strategy/v1/Strategies?sourceMarketplaceId=${srcId}&destinationMarketplace=${destId}`,
                {},
                { 'Seller-Account-Id': sellersCode, 'Account-Id': accountCode },
              )
            : { data: {} };
        self.pricingList = data;
      } catch (err) {
        console.log('get pricing list failed: ', err);
      }

      try {
        const { data } =
          srcId && sellersCode
            ? yield self.transport5007.fetch(
                `strategy/v1/strategies/getAllRangedStrategies?sourceMarketplaceId=${srcId}&destinationMarketplace=${destId}`,
                {},
                { 'Seller-Account-Id': sellersCode },
              )
            : { data: {} };
        self.rangedPricingList = data.strategies;
      } catch (err) {
        console.log('get pricing list failed: ', err);
      }
    });

    const editPricing = flow(function* editPricing(newPricingObj, type, selectedAccountCode) {
      try {
        const headers = {
          'Seller-Account-Id':
            selectedAccountCode || getParent(self).markets.defaultSellersAccount.id,
        };
        yield self.transport5007.update(
          `strategy/v1/Strategies/${type}`,
          {
            ...newPricingObj,
            currency: self.currencyId,
            sellerAccountId: selectedAccountCode || getParent(self).markets.defaultSellersAccount.id
          },
          {},
          headers,
        );
      } catch (err) {
        console.log('err: ', err);
      }
    });

    const editRangedPricing = flow(function* editRangedPricing(newPricingObj) {
      try {
        const headers = {
          'Seller-Account-Id': newPricingObj.sellerAccountId,
        };
        if (!self.changedRangedPricingList.length) {
          return;
        }
        yield self.transport5007.update(
          `strategy/v1/Strategies/updateRangedStrategies`,
          {
            ...newPricingObj,
            frontRangedStrategies: self.changedRangedPricingList,
          },
          {},
          headers,
        );
      } catch (err) {
        console.log('err: ', err);
      }
    });

    const updateListedItemsStrategy = flow(function* updateListedItemsStrategy(newPricingObj) {
      try {
        yield self.transport5007.post(
          `seller-listing/v1/Items/listed-items-pricing-strategy`,newPricingObj
        );
      } catch (err) {
        console.log('err: ', err);
      }
    });

    const fetchStrategyDefaultValues = flow(function* fetchStrategyDefaultValues(
      type,
      srcId,
      destId,
      selectedAccountCode,
    ) {
      try {
        const headers = {
          'Seller-Account-Id': selectedAccountCode,
        };
            const { data } = yield self.transport5007.fetch(
              `strategy/v1/Strategies/${type}/default?sourceMarketplaceId=${srcId}&destinationMarketplace=${destId}`,
              {},
              headers,
            );
            self.pricingList[type] = data;
            return data;
      } catch (err) {
        console.log('fetching default values failed: ', err);
        return {};
      }
    });

    return {
      editRangedPricing,
      fetchPricingList,
      editPricing,
      fetchStrategyDefaultValues,
      setIsUpdatingAllStore,
      updateListedItemsStrategy
    };
  });

export default Pricing;
